import React, { RefObject, useMemo } from 'react';
import { Badge, Box, Group, Stack, Tooltip } from '@mantine/core';
import useStepErrors from '@/hooks/useStepErrors.ts';
import useIsNewStep from '@/hooks/useIsNewStep.ts';
import { useStepContext } from '@/context/StepContext/useStepContext.ts';

import { useIsDragPreview } from '@/pages/Editor/components/tree/useIsDragPreview.ts';

import StepCardTitle from '@/components/StepCard/StepCardTitle.tsx';
import { useStepCardDnD } from '@/pages/Editor/components/EditorStepCard/hooks/useStepCardDnD.ts';
import { AutomationStep, ParentStep } from '@/types/automationSteps.ts';
import WarningPills from '@/pages/Editor/components/EditorStepCard/components/WarningPill.tsx';
import { getStepCardStyles } from '@/components/StepCard/styles.ts';
import StepCardTopLabel from '@/components/StepCard/StepCardTopLabel.tsx';
import { useActiveStep } from '@/layouts/AutomationPageWrapper/hooks/useActiveStep.ts';
import { ConditionAction, StepType } from '@/types/automationFields.ts';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import { getActiveConditionId, StepDisabledInfo } from '@/utils/stepUtils.ts';

export interface StepCardProps {
  step: AutomationStep;
  index: number;
  parentSteps: ParentStep[];
  activeConditionIds?: Record<string, string>;
  setActiveConditionIds?: (c: Record<string, string>) => void;
  disabledInfo?: StepDisabledInfo;
  canReorder?: boolean;
}

export default function EditorStepCard({
  step,
  index,
  parentSteps,
  activeConditionIds,
  setActiveConditionIds,
  disabledInfo,
  canReorder = true,
}: StepCardProps) {
  const isDragPreview = useIsDragPreview();

  // dnd
  const isLocalSubflowCall = step.step_type === StepType.LocalSubflowCall;
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, dragStyle } = useStepCardDnD(
    step.step_id,
    isDragPreview || isLocalSubflowCall || !canReorder,
  );

  const { targetRef } = useStepContext();

  const { stepId, setActiveStep } = useActiveStep();

  const isActiveStep = stepId === step.step_id;

  const isNewStep = useIsNewStep(step.step_id);

  const rawFieldErrors = useStepErrors(step);
  const fieldErrors = disabledInfo?.isEffectivelyDisabled ? [] : rawFieldErrors;

  const status = useMemo(() => {
    if (disabledInfo?.isEffectivelyDisabled) return 'disabled';
    if (isActiveStep && !isNewStep) return 'active';
    if (fieldErrors.length > 0) return 'warning';
    return 'default';
  }, [disabledInfo?.isEffectivelyDisabled, fieldErrors.length, isActiveStep, isNewStep]);

  const stepCardStyles = getStepCardStyles(status);

  const isTriggerStep = step.step_type === 'trigger';

  return (
    <Box
      style={{ position: 'relative', textAlign: 'left', width: '100%', ...dragStyle }}
      ref={setNodeRef}
    >
      {!isDragPreview && <StepCardTopLabel step={step} index={index} parentSteps={parentSteps} />}

      {!isDragPreview && (
        <WarningPills
          fieldErrorsCount={fieldErrors.length}
          parentSteps={parentSteps}
          stepId={step.step_id}
        />
      )}

      <Stack
        ref={setActivatorNodeRef as any}
        {...attributes}
        {...listeners}
        style={{
          cursor: isTriggerStep || isLocalSubflowCall || !canReorder ? 'pointer' : 'grab',
          ...stepCardStyles,
          padding: '12px 16px 12px 16px',
        }}
      >
        <Stack
          ref={isActiveStep ? (targetRef as RefObject<HTMLDivElement>) : undefined}
          onClick={() => setActiveStep(step.step_id)}
        >
          <Group justify="space-between" align="center" wrap="nowrap">
            <StepCardTitle step={step} />
            {disabledInfo?.isEffectivelyDisabled && (
              <Tooltip
                label={
                  disabledInfo.isInheritedDisabled
                    ? 'Disabled by an upstream step'
                    : 'Disabled directly'
                }
                openDelay={250}
              >
                <Badge size="xs" color="gray" variant="light" style={{ flexShrink: 0 }}>
                  Disabled
                </Badge>
              </Tooltip>
            )}
          </Group>
        </Stack>
      </Stack>

      {setActiveConditionIds &&
        ((step.step_type === StepType.Conditional &&
          !step.conditions.every((c) => c.action !== ConditionAction.ChildSteps)) ||
          step.step_type === StepType.FailureHandler) && (
          <Stack ml={40} mt={'xs'}>
            <StyledSelect
              label={step.step_type === StepType.FailureHandler ? 'Error boundary path' : 'Branch'}
              placeholder="Select a path"
              data={
                step.step_type === StepType.FailureHandler
                  ? [
                      { value: 'protected', label: 'Main work' },
                      { value: 'handler', label: 'Recovery steps' },
                    ]
                  : step.conditions
                      .map((c, i) =>
                        c.action === ConditionAction.ChildSteps
                          ? { value: c.condition_id, label: `Condition ${i + 1}: ${c.name}` }
                          : null,
                      )
                      .filter((c) => c !== null)
              }
              value={getActiveConditionId(step, activeConditionIds || {})}
              onChange={(value) => {
                if (!setActiveConditionIds || !value) return;
                setActiveConditionIds({ ...(activeConditionIds || {}), [step.step_id]: value });
              }}
              required={true}
              allowDeselect={false}
              clearable={false}
            />
          </Stack>
        )}
    </Box>
  );
}
